/**
 * Build GeoJSON polygon from array of [lat, lng] points.
 */
export const buildPolygonGeoJson = (points) => {
    if (!Array.isArray(points) || points.length < 3) return null;
    const closed = [...points, points[0]];
    return {
        type: 'FeatureCollection',
        features: [
            {
                type: 'Feature',
                geometry: {
                    type: 'Polygon',
                    coordinates: [closed.map(([lat, lng]) => [lng, lat])],
                },
                properties: {},
            },
        ],
    };
};

/**
 * Build circular GeoJSON polygon (geodesic) from center and radius.
 */
export const makeCircleGeoJson = ([lat, lng], radiusMeters = 50, points = 64) => {
    const coords = [];
    const earthRadius = 6371000;
    const toRad = Math.PI / 180;
    const toDeg = 180 / Math.PI;

    const lat1 = lat * toRad;
    const lng1 = lng * toRad;

    for (let i = 0; i <= points; i++) {
        const bearing = (2 * Math.PI * i) / points;
        const lat2 = Math.asin(
            Math.sin(lat1) * Math.cos(radiusMeters / earthRadius) +
                Math.cos(lat1) * Math.sin(radiusMeters / earthRadius) * Math.cos(bearing)
        );
        const lng2 =
            lng1 +
            Math.atan2(
                Math.sin(bearing) * Math.sin(radiusMeters / earthRadius) * Math.cos(lat1),
                Math.cos(radiusMeters / earthRadius) - Math.sin(lat1) * Math.sin(lat2)
            );

        coords.push([lng2 * toDeg, lat2 * toDeg]);
    }

    return {
        type: 'FeatureCollection',
        features: [
            {
                type: 'Feature',
                geometry: { type: 'Polygon', coordinates: [coords] },
                properties: {},
            },
        ],
    };
};

/**
 * Haversine distance in meters between two lat/lon points.
 */
export const haversineMeters = (lat1, lon1, lat2, lon2) => {
    const R = 6371000;
    const toRad = (d) => (d * Math.PI) / 180;

    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);

    const a =
        Math.sin(dLat / 2) ** 2 +
        Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;

    return 2 * R * Math.asin(Math.sqrt(a));
};

/**
 * Compress geolocation coords: dedupe by time window + remove stationary points.
 * @param {Array} coords - Sorted array of {latitude, longitude, timestamp}
 * @param {number} intervalMs - Time window in ms (e.g. 30000 = 30 sec)
 */
const compressByTimeWindow = (coords, intervalMs, keep = 'average') => {
    if (!intervalMs || intervalMs < 1000) return coords;
    const byWindow = new Map();
    for (const p of coords) {
        const key = Math.floor(new Date(p.timestamp).getTime() / intervalMs) * intervalMs;
        if (!byWindow.has(key)) byWindow.set(key, []);
        byWindow.get(key).push(p);
    }
    return [...byWindow.entries()]
        .sort((a, b) => a[0] - b[0])
        .map(([, points]) => {
            if (keep === 'first') return points[0];
            if (keep === 'last') return points[points.length - 1];
            const n = points.length;
            const lat = points.reduce((s, p) => s + p.latitude, 0) / n;
            const lng = points.reduce((s, p) => s + p.longitude, 0) / n;
            return { ...points[0], latitude: lat, longitude: lng };
        });
};

const compressByMinInterval = (coords, minMinutes) => {
    if (!coords?.length || minMinutes <= 0) return coords;
    const out = [coords[0]];
    const intervalMs = minMinutes * 60 * 1000;
    for (let i = 1; i < coords.length; i++) {
        const prev = out[out.length - 1];
        const curr = coords[i];
        const prevTime = new Date(prev.timestamp).getTime();
        const currTime = new Date(curr.timestamp).getTime();
        if (currTime - prevTime >= intervalMs) out.push(curr);
    }
    return out;
};

/**
 * Compress points by min interval - generic version for any point format.
 * @param {Array} points - Sorted array of points
 * @param {number} minMinutes - Min minutes between kept points (0 = no filtering)
 * @param {function} getTimestamp - (point) => timestamp (number, ISO string, or "HH:mm")
 */
export const compressPointsByMinInterval = (points, minMinutes, getTimestamp) => {
    if (!points?.length || minMinutes <= 0) return points;
    const getTime = (p) => {
        const ts = getTimestamp(p);
        if (ts == null) return 0;
        if (typeof ts === 'number' && Number.isFinite(ts)) return ts;
        const str = String(ts).trim();
        if (/^\d{1,2}:\d{2}(:\d{2})?$/.test(str)) {
            const withSec = str.length <= 5 ? `${str}:00` : str;
            return new Date(`1970-01-01T${withSec}Z`).getTime();
        }
        const d = new Date(ts);
        return Number.isFinite(d.getTime()) ? d.getTime() : 0;
    };
    const out = [points[0]];
    const intervalMs = minMinutes * 60 * 1000;
    for (let i = 1; i < points.length; i++) {
        const prevTime = getTime(out[out.length - 1]);
        const currTime = getTime(points[i]);
        if (currTime - prevTime >= intervalMs) out.push(points[i]);
    }
    return out;
};

const compressByDistance = (coords, minMeters) => {
    if (!coords?.length) return [];
    const out = [coords[0]];
    for (let i = 1; i < coords.length; i++) {
        const prev = out[out.length - 1];
        const curr = coords[i];
        const dist = haversineMeters(
            prev.latitude,
            prev.longitude,
            curr.latitude,
            curr.longitude
        );
        if (dist >= minMeters) out.push(curr);
    }
    return out;
};

export const compressGeoCoords = (coords, opts = {}) => {
    const {
        minMeters = 15,
        dedupeIntervalSeconds = 30,
        minIntervalMinutes = 0,
    } = opts;
    if (!coords?.length) return [];
    let result = [...coords].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
    if (dedupeIntervalSeconds > 0) {
        result = compressByTimeWindow(result, dedupeIntervalSeconds * 1000, 'average');
    }
    if (minMeters > 0) result = compressByDistance(result, minMeters);
    if (minIntervalMinutes > 0) result = compressByMinInterval(result, minIntervalMinutes);
    return result;
};

/**
 * Point-in-polygon check (ray casting). Polygon is array of [lat, lon] pairs.
 */
export const pointInPolygon = (lat, lon, polygon) => {
    if (!Array.isArray(polygon) || polygon.length < 3) return false;

    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
        const [latI, lonI] = polygon[i];
        const [latJ, lonJ] = polygon[j];

        const intersects =
            (lonI > lon) !== (lonJ > lon) &&
            lat < ((latJ - latI) * (lon - lonI)) / (lonJ - lonI + 0.0) + latI;

        if (intersects) inside = !inside;
    }
    return inside;
};

const bboxFromPolygon = (polygon) => {
    let minLat = Infinity, maxLat = -Infinity, minLon = Infinity, maxLon = -Infinity;
    for (const [la, lo] of polygon) {
        const lat = Number(la);
        const lon = Number(lo);
        if (!Number.isFinite(lat) || !Number.isFinite(lon)) continue;
        minLat = Math.min(minLat, lat);
        maxLat = Math.max(maxLat, lat);
        minLon = Math.min(minLon, lon);
        maxLon = Math.max(maxLon, lon);
    }
    return { minLat, maxLat, minLon, maxLon };
};

/**
 * Returns array of location titles that contain the given point (lat, lon).
 * @param {Array} locations - Airport locations from Redux (each with geofenceDetails)
 * @param {number} lat - Latitude
 * @param {number} lon - Longitude
 * @returns {string[]} Matched location titles
 */
export const getLocationsForPoint = (locations, lat, lon) => {
    if (!Array.isArray(locations)) return [];

    const plat = Number(lat);
    const plon = Number(lon);
    if (!Number.isFinite(plat) || !Number.isFinite(plon)) return [];

    const matched = [];

    for (const loc of locations) {
        const g = loc?.geofenceDetails;
        if (!g?.type) continue;

        if (g.type === "circular") {
            const c = g.circular || {};
            const clat = Number(c.lat);
            const clon = Number(c.long);
            const radius = Number(c.radius);

            if (!Number.isFinite(clat) || !Number.isFinite(clon) || !Number.isFinite(radius)) continue;

            const dist = haversineMeters(plat, plon, clat, clon);
            if (dist <= radius && loc.title) matched.push(loc.title);
        } else if (g.type === "polygon") {
            const pts = (g.polygon || [])
                .map(([la, lo]) => [Number(la), Number(lo)])
                .filter(([la, lo]) => Number.isFinite(la) && Number.isFinite(lo));

            if (pts.length < 3) continue;

            const bbox = bboxFromPolygon(pts);
            if (plat < bbox.minLat || plat > bbox.maxLat || plon < bbox.minLon || plon > bbox.maxLon) continue;

            if (pointInPolygon(plat, plon, pts) && loc.title) matched.push(loc.title);
        }
    }

    return matched;
};
